import { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Loader2, Check, Send, Sparkles } from "lucide-react";

export default function CaptureForm({ reportToken, accent = "#10B981", company = "the team", personaName = "" }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  // AI-disclosure clause (TCPA + FCC 2024 AI-messaging guidance). Only
  // appended when the workspace has picked an AI persona name.
  const aiClause = personaName
    ? ` including automated messages from ${personaName} (an AI acquisitions assistant)`
    : "";

  // Verbatim consent language we snapshot at submit-time so we can produce
  // an audit trail to carriers / TCR if ever asked to prove opt-in.
  const consentText =
    `I agree to receive SMS from ${company}${aiClause} about investment properties matching my buy-box criteria. ` +
    `Message and data rates may apply. Reply STOP to opt out, HELP for assistance. ` +
    `Consent is not a condition of purchase. See ispeedtoclose.com/legal/sms-consent for terms.`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const params = new URLSearchParams(window.location.search);
      await axios.post(`${API}/public/capture/${reportToken}`, {
        name,
        phone,
        email: email || undefined,
        message,
        utm_source: params.get("utm_source") || undefined,
        utm_medium: params.get("utm_medium") || undefined,
        utm_campaign: params.get("utm_campaign") || undefined,
        sms_consent: smsConsent,
        sms_consent_text: smsConsent ? consentText : undefined,
      });
      setOk(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  if (ok) {
    return (
      <div
        className="card border-2 text-center py-12"
        style={{ borderColor: accent }}
        data-testid="capture-form-success"
      >
        <div
          className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4"
          style={{ background: accent + "1a", color: accent }}
        >
          <Check size={28} />
        </div>
        <div className="font-display font-bold text-2xl">You&apos;re on the list.</div>
        <p className="text-[var(--ink-2)] mt-2 max-w-md mx-auto">
          {company} will text you the moment a deal like this lands. No spam — just first looks.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="card border-2 print:hidden"
      style={{ borderColor: accent }}
      data-testid="capture-form"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: accent, color: "#fff" }}
        >
          <Sparkles size={20} />
        </div>
        <div className="flex-1">
          <div className="label-eyebrow flex items-center gap-1.5"><Send size={11} /> First-dibs list</div>
          <h2 className="font-display font-extrabold text-xl lg:text-2xl mt-1 tracking-tight">
            Want first dibs on deals like this?
          </h2>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Drop your number — {company} will text you the moment another deal in this market hits the desk.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        <input
          type="text"
          className="form-input"
          placeholder="Your name *"
          required
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="capture-name-input"
        />
        <input
          type="tel"
          className="form-input"
          placeholder="Phone *"
          required
          maxLength={40}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid="capture-phone-input"
        />
        <input
          type="email"
          className="form-input sm:col-span-2"
          placeholder="Email (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="capture-email-input"
        />
        <textarea
          rows={2}
          className="form-input sm:col-span-2 resize-none"
          placeholder="Anything specific you're looking for? (optional)"
          maxLength={500}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="capture-message-input"
        />
      </div>

      {err && (
        <div className="banner-err mt-3" data-testid="capture-form-error">{err}</div>
      )}

      {/* A2P 10DLC opt-in checkbox — required when buyer wants SMS alerts */}
      <label
        className="flex items-start gap-3 mt-5 p-3 rounded-lg border cursor-pointer transition-colors"
        style={{
          background: smsConsent ? accent + "0c" : "transparent",
          borderColor: smsConsent ? accent + "55" : "var(--line)",
        }}
        data-testid="capture-sms-consent-row"
      >
        <input
          type="checkbox"
          checked={smsConsent}
          onChange={(e) => setSmsConsent(e.target.checked)}
          className="mt-0.5 w-4 h-4 shrink-0"
          data-testid="capture-sms-consent-checkbox"
          required
        />
        <span className="text-[12px] leading-snug text-[var(--ink-2)]">
          I agree to receive SMS from {company}{aiClause} about investment properties
          matching my buy-box criteria. Message and data rates may apply.
          Reply <strong>STOP</strong> to opt out, <strong>HELP</strong> for assistance.
          Consent is not a condition of purchase.
          See <a href="/legal/sms-consent" target="_blank" rel="noreferrer" className="underline">SMS terms</a>.
        </span>
      </label>

      <div className="flex items-center gap-3 mt-5">
        <button
          type="submit"
          className="btn-money"
          disabled={busy || !smsConsent}
          style={{ background: accent, borderColor: accent }}
          data-testid="capture-submit-button"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {busy ? "Sending…" : "Get first dibs"}
        </button>
        <span className="text-xs text-[var(--ink-3)]">No spam. Unsubscribe anytime.</span>
      </div>
    </form>
  );
}
